import type { CampaignPrizeType, ParsedCampaignRules } from '@ping/helpers/campaign-rules.helper';

export type CampaignPrizeTierStyle = 'prizeGold' | 'prizeSilver' | 'prizeBronze' | 'prizeVoucher' | 'prizeDefault';

export interface CampaignPrizeLike {
  tierName: string;
  amount: number;
  currency: string;
  quantity: number;
  tierOrder?: number;
  prizeType?: CampaignPrizeType | number | string | null;
}

export interface GroupedCampaignPrizes<T extends CampaignPrizeLike> {
  digitalAsset: T[];
  voucher: T[];
  other: T[];
}

export interface CampaignPrizePoolTotal {
  currency: string;
  amount: number;
}

export function isNonCurrencyPrizeType(prizeType: CampaignPrizeType): boolean {
  return prizeType === 'voucher' || prizeType === 'other';
}

export function adminPrizeTypeToCampaignPrizeType(prizeType: number): CampaignPrizeType {
  if (prizeType === 2) {
    return 'voucher';
  }

  if (prizeType === 3) {
    return 'other';
  }

  return 'digital_asset';
}

function prizeTypeFromRawValue(prizeType: CampaignPrizeLike['prizeType']): CampaignPrizeType | null {
  if (prizeType === 'voucher' || prizeType === 2 || prizeType === 'Voucher') {
    return 'voucher';
  }

  if (prizeType === 'other' || prizeType === 3 || prizeType === 'Other') {
    return 'other';
  }

  if (
    prizeType === 'digital_asset' ||
    prizeType === 1 ||
    prizeType === 'DigitalAsset' ||
    prizeType === 'digitalAsset'
  ) {
    return 'digital_asset';
  }

  return null;
}

export function normalizeCampaignPrizeType(
  prize: CampaignPrizeLike,
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): CampaignPrizeType {
  const explicitType = prizeTypeFromRawValue(prize.prizeType);
  if (explicitType) {
    return explicitType;
  }

  if (prize.tierOrder) {
    const tierPrizeType = rules?.prizeTypesByTierOrder?.[prize.tierOrder];
    if (tierPrizeType) {
      return tierPrizeType;
    }
  }

  if (/bank\s*card|voucher/i.test(prize.tierName)) {
    return 'voucher';
  }

  return 'digital_asset';
}

export function enrichCampaignPrizesWithRules<T extends CampaignPrizeLike>(
  prizes: T[],
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): Array<T & { prizeType: CampaignPrizeType }> {
  return prizes.map(prize => ({
    ...prize,
    prizeType: normalizeCampaignPrizeType(prize, rules),
  }));
}

export function getCampaignPrizeMonetaryContribution(prize: CampaignPrizeLike): number {
  if (!Number.isFinite(prize.amount) || prize.amount <= 0) {
    return 0;
  }

  if (!Number.isFinite(prize.quantity) || prize.quantity <= 0) {
    return 0;
  }

  return prize.amount * prize.quantity;
}

export function getCampaignPrizePoolTotals(prizes: CampaignPrizeLike[]): CampaignPrizePoolTotal[] {
  const totalsByCurrency = new Map<string, number>();

  prizes.forEach(prize => {
    const contribution = getCampaignPrizeMonetaryContribution(prize);
    if (contribution <= 0) {
      return;
    }

    const currency = prize.currency.trim().toUpperCase() || 'USD';
    totalsByCurrency.set(currency, (totalsByCurrency.get(currency) ?? 0) + contribution);
  });

  return Array.from(totalsByCurrency.entries())
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount);
}

export interface CampaignPrizePoolBreakdown {
  digitalAsset: CampaignPrizePoolTotal[];
  voucher: CampaignPrizePoolTotal[];
  other: CampaignPrizePoolTotal[];
}

export function getCampaignPrizePoolBreakdown(
  prizes: CampaignPrizeLike[],
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): CampaignPrizePoolBreakdown {
  const grouped = groupCampaignPrizes(prizes, rules);

  return {
    digitalAsset: getCampaignPrizePoolTotals(grouped.digitalAsset),
    voucher: getCampaignPrizePoolTotals(grouped.voucher),
    other: getCampaignPrizePoolTotals(grouped.other),
  };
}

export function getCampaignPrizeTierStyle(prize: CampaignPrizeLike): CampaignPrizeTierStyle {
  const prizeType = normalizeCampaignPrizeType(prize);

  if (prizeType === 'voucher') {
    return 'prizeVoucher';
  }

  const normalizedTierName = prize.tierName.trim().toLowerCase();

  if (normalizedTierName.includes('1st') || normalizedTierName.includes('gold')) {
    return 'prizeGold';
  }

  if (normalizedTierName.includes('2nd') || normalizedTierName.includes('silver')) {
    return 'prizeSilver';
  }

  if (normalizedTierName.includes('3rd') || normalizedTierName.includes('bronze')) {
    return 'prizeBronze';
  }

  return 'prizeDefault';
}

function formatMoneyAmount(amount: number, currency: string): string {
  const normalizedCurrency = currency.trim().toUpperCase();

  if (normalizedCurrency === 'USD') {
    return `$${amount.toLocaleString()}`;
  }

  return `${amount.toLocaleString()} ${normalizedCurrency}`;
}

export function formatCampaignPrizeValue(
  prize: CampaignPrizeLike,
  t: (key: string, ...args: unknown[]) => string,
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): string {
  const prizeType = normalizeCampaignPrizeType(prize, rules);
  const monetaryValue = formatMoneyAmount(prize.amount, prize.currency);

  if (prizeType === 'voucher') {
    return t('Voucher · valued at %s each', monetaryValue);
  }

  if (prizeType === 'other') {
    return t('Valued at %s each', monetaryValue);
  }

  return monetaryValue;
}

export function formatCampaignPrizeSummary(
  prize: CampaignPrizeLike,
  t: (key: string, ...args: unknown[]) => string,
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): string {
  const prizeType = normalizeCampaignPrizeType(prize, rules);
  const monetaryValue = formatMoneyAmount(prize.amount, prize.currency);

  if (prizeType === 'voucher') {
    return t('%s · voucher valued at %s', prize.tierName, monetaryValue);
  }

  if (prizeType === 'other') {
    return t('%s · valued at %s', prize.tierName, monetaryValue);
  }

  return `${prize.tierName} · ${formatCampaignPrizeValue(prize, t, rules)}`;
}

export function formatCampaignPrizeWinnerMessage(
  prize: CampaignPrizeLike,
  t: (key: string, ...args: unknown[]) => string,
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): string {
  const prizeType = normalizeCampaignPrizeType(prize, rules);
  const monetaryValue = formatMoneyAmount(prize.amount, prize.currency);

  if (prizeType === 'voucher') {
    return t('You won a %s voucher valued at %s', prize.tierName, monetaryValue);
  }

  if (prizeType === 'other') {
    return t('You won %s — valued at %s', prize.tierName, monetaryValue);
  }

  return t('You won %s — %s', formatCampaignPrizeValue(prize, t, rules), prize.tierName);
}

export function formatCampaignPrizeDrawLabel(
  prize: CampaignPrizeLike,
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): string {
  const prizeType = normalizeCampaignPrizeType(prize, rules);
  const monetaryValue = formatMoneyAmount(prize.amount, prize.currency);

  if (prizeType === 'voucher') {
    return `${prize.tierName} voucher (${monetaryValue})`;
  }

  if (prizeType === 'other') {
    return `${prize.tierName} (${monetaryValue})`;
  }

  return `${prize.tierName} — ${monetaryValue}`;
}

export function groupCampaignPrizes<T extends CampaignPrizeLike>(
  prizes: T[],
  rules?: Pick<ParsedCampaignRules, 'prizeTypesByTierOrder'>
): GroupedCampaignPrizes<T> {
  const grouped: GroupedCampaignPrizes<T> = { digitalAsset: [], voucher: [], other: [] };

  prizes.forEach(prize => {
    const prizeType = normalizeCampaignPrizeType(prize, rules);

    if (prizeType === 'voucher') {
      grouped.voucher.push(prize);
      return;
    }

    if (prizeType === 'other') {
      grouped.other.push(prize);
      return;
    }

    grouped.digitalAsset.push(prize);
  });

  return grouped;
}

export function formatCampaignPrizePoolTotal(currency: string, amount: number): string {
  return formatMoneyAmount(amount, currency);
}

export function formatCampaignPrizeWinnersLabel(
  quantity: number,
  t: (key: string, ...args: unknown[]) => string
): string {
  return quantity > 1 ? t('%s winners', quantity) : t('1 winner');
}
